<template>
  <v-container fluid>
    <v-card v-if="record" variant="outlined">
      <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4">
        <span>Production Material Receipt {{ record.code }}</span>
        <v-spacer />
        <v-chip :color="approvalColor(record.approval_status)" size="small">{{ record.approval_status || (record.status ? 'Pending' : 'Draft') }}</v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="6" md="3"><Info label="Inventory Issue" :value="record.delivery_master?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Production Requisition" :value="record.delivery_master?.requisition_master?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Receive Date" :value="dateOnly(record.receive_date)" /></v-col>
          <v-col cols="6" md="3"><Info label="Posted" :value="record.posted_at ? 'Yes' : 'No'" /></v-col>
          <v-col cols="12"><Info label="Remarks" :value="record.remarks || '-'" /></v-col>
        </v-row>

        <v-table density="compact" class="border-sm mt-4">
          <thead>
            <tr>
              <th>Material</th>
              <th>Inventory Lot / Batch</th>
              <th>Manufactured</th>
              <th>Expiry</th>
              <th class="text-center">Purchase Purity</th>
              <th class="text-end">Received Qty</th>
              <th class="text-end">Unit Cost</th>
              <th class="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="detail in record.details || []" :key="detail.id">
              <td>{{ detail.product?.code }} - {{ detail.product?.name }}</td>
              <td>{{ detail.lot_no || '-' }} / {{ detail.batch_no || '-' }}</td>
              <td>{{ dateOnly(detail.manufacturing_date) || '-' }}</td>
              <td>{{ dateOnly(detail.expiry_date) || '-' }}</td>
              <td class="text-center">{{ number(detail.purchase_purity, 4) }}%</td>
              <td class="text-end">{{ number(detail.quantity) }}</td>
              <td class="text-end">{{ number(detail.rate, 6) }}</td>
              <td class="text-end">{{ number(detail.amount, 2) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-weight-bold">
              <td colspan="7" class="text-end">Total</td>
              <td class="text-end">{{ number(totalAmount, 2) }}</td>
            </tr>
          </tfoot>
        </v-table>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "info-box" }, [
      h("div", { class: "info-label" }, props.label),
      h("div", { class: "font-weight-medium" }, props.value ?? "-"),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const record = ref(null);
const totalAmount = computed(() => (record.value?.details || []).reduce((sum, item) => sum + Number(item.amount || 0), 0));

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function printPage() {
  window.print();
}

onMounted(async () => {
  try {
    const response = await store.dispatch("getRawMetarialRequisitionReceiveById", route.params.id);
    record.value = response?.data || response;
  } catch (error) {
    toast.error(error?.response?.data?.message || "Material receipt details could not be loaded.");
  }
});
</script>

<style scoped>
.info-box { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; height: 100%; padding: 8px 10px; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.7rem; text-transform: uppercase; }
</style>
